import { AuthenticationAssuranceLevel, ScopeType, AuditPolicy, IdempotencyPolicy, ConcurrencyPolicy, PrivacyClassification, ActorType, } from './operation.js';
export const PORTFOLIO_PERFORMANCE_LIST = 'investment.performance.list';
export const PORTFOLIO_PERFORMANCE_EXPORT = 'investment.performance.export';
export const CLIENT_PORTFOLIO_PERFORMANCE_READ_OWN = 'client.portfolio-performance.read-own';
export const REPORTING_PERFORMANCE_RETURN_CALCULATE = 'reporting.performance-return.calculate';
export const REPORTING_PERFORMANCE_RETURN_LIST = 'reporting.performance-return.list';
export const REPORTING_PERFORMANCE_RETURN_CORRECTION_REQUEST = 'reporting.performance-return.correction-request';
export const REPORTING_PERFORMANCE_RETURN_CORRECTION_APPROVE = 'reporting.performance-return.correction-approve';
export const REPORTING_PERFORMANCE_RETURN_CORRECTION_FINALIZE = 'reporting.performance-return.correction-finalize';
export const REPORTING_FIXED_INCOME_VALUATION_CALCULATE = 'reporting.fixed-income-valuation.calculate';
export const REPORTING_FIXED_INCOME_LIFECYCLE_SCHEDULE = 'reporting.fixed-income-lifecycle.schedule';
export const REPORTING_FIXED_INCOME_LIFECYCLE_APPROVE = 'reporting.fixed-income-lifecycle.approve';
export const REPORTING_FIXED_INCOME_LIFECYCLE_POST = 'reporting.fixed-income-lifecycle.post';
export const REPORTING_FIXED_INCOME_DEBT_EVENT_PROCESS = 'reporting.fixed-income-debt-event.process';
export const REPORTING_VALUATION_RUN_CREATE = 'reporting.valuation-run.create';
export const REPORTING_VALUATION_RUN_LIST = 'reporting.valuation-run.list';
export const REPORTING_VALUATION_RUN_APPROVE = 'reporting.valuation-run.approve';
export const REPORTING_VALUATION_RUN_PUBLISH = 'reporting.valuation-run.publish';
export const REPORTING_VALUATION_RUN_REOPEN_REQUEST = 'reporting.valuation-run.reopen-request';
export const REPORTING_VALUATION_RUN_REOPEN_APPROVE = 'reporting.valuation-run.reopen-approve';
const BRANCH_PORTFOLIO_SCOPES = [
    ScopeType.ORGANIZATION,
    ScopeType.BRANCH,
    ScopeType.CLIENT,
    ScopeType.PORTFOLIO,
];
const BRANCH_PORTFOLIO_SCOPE_KINDS = ['ORGANIZATION', 'BRANCH', 'CLIENT', 'PORTFOLIO'];
function portfolioOperation(id, name, options = {}) {
    const readOnly = options.readOnly ?? false;
    const exportOnly = options.exportOnly ?? false;
    const permission = options.permission ?? 'organization:reports:read';
    return {
        id,
        name,
        labelKey: exportOnly
            ? 'operations.portfolioPerformance.export.label'
            : 'operations.portfolioPerformance.list.label',
        descriptionKey: exportOnly
            ? 'operations.portfolioPerformance.export.description'
            : 'operations.portfolioPerformance.list.description',
        permission,
        permissionId: (options.permissionId ?? permission),
        module: options.module ?? 'REPORTING',
        requiredModules: ['INVESTMENT', 'REPORTING'],
        requiresEntitledModule: true,
        requiredAal: options.aal ?? (readOnly && !exportOnly
            ? AuthenticationAssuranceLevel.AAL1
            : AuthenticationAssuranceLevel.AAL2),
        scopeTypes: [...BRANCH_PORTFOLIO_SCOPES],
        allowedScopeKinds: [...BRANCH_PORTFOLIO_SCOPE_KINDS],
        allowedPersonas: options.allowedPersonas ?? ['ORG_ADMIN'],
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        requestedScope: options.requestedScope,
        forcesClientScope: true,
        makerCheckerRequired: options.makerChecker ?? false,
        idempotencyPolicy: readOnly || exportOnly ? IdempotencyPolicy.NOT_APPLICABLE : IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: readOnly || exportOnly ? ConcurrencyPolicy.NONE : ConcurrencyPolicy.OPTIMISTIC,
        auditPolicy: exportOnly ? AuditPolicy.SENSITIVE : readOnly ? AuditPolicy.READ : AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: id,
        allowedActorTypes: options.actorTypes ?? [ActorType.USER],
        httpMethods: exportOnly ? ['POST'] : [readOnly ? 'GET' : 'POST'],
        isReadOnly: readOnly || exportOnly,
        isFinancial: true,
        supportDelegationEligible: options.supportDelegationEligible ?? false,
        delegationPolicy: options.delegationPolicy ?? 'NOT_ALLOWED',
        accessType: exportOnly ? 'EXPORT' : readOnly ? 'READ' : 'WRITE',
    };
}
function branchMutationScope() {
    return {
        organizationId: [{ source: 'query', field: 'organizationId' }],
        branchId: [{ source: 'query', field: 'branchId' }],
    };
}
export const PORTFOLIO_PERFORMANCE_OPERATIONS = {
    [REPORTING_VALUATION_RUN_CREATE]: portfolioOperation(REPORTING_VALUATION_RUN_CREATE, 'Create Portfolio Valuation Run', { requestedScope: { portfolioId: [{ source: 'body', field: 'portfolioAccountId' }] } }),
    [REPORTING_VALUATION_RUN_LIST]: portfolioOperation(REPORTING_VALUATION_RUN_LIST, 'List Portfolio Valuation Runs', { readOnly: true, requestedScope: { portfolioId: [{ source: 'query', field: 'portfolioAccountId' }] }, aal: AuthenticationAssuranceLevel.AAL2 }),
    [REPORTING_VALUATION_RUN_APPROVE]: portfolioOperation(REPORTING_VALUATION_RUN_APPROVE, 'Approve Portfolio Valuation Run', { makerChecker: true, requestedScope: branchMutationScope() }),
    [REPORTING_VALUATION_RUN_PUBLISH]: portfolioOperation(REPORTING_VALUATION_RUN_PUBLISH, 'Publish Portfolio Valuation Run', { makerChecker: true, requestedScope: branchMutationScope() }),
    [REPORTING_VALUATION_RUN_REOPEN_REQUEST]: portfolioOperation(REPORTING_VALUATION_RUN_REOPEN_REQUEST, 'Request Portfolio Valuation Run Reopening', { makerChecker: true, requestedScope: branchMutationScope() }),
    [REPORTING_VALUATION_RUN_REOPEN_APPROVE]: portfolioOperation(REPORTING_VALUATION_RUN_REOPEN_APPROVE, 'Approve Portfolio Valuation Run Reopening', { makerChecker: true, requestedScope: branchMutationScope() }),
    [REPORTING_FIXED_INCOME_DEBT_EVENT_PROCESS]: portfolioOperation(REPORTING_FIXED_INCOME_DEBT_EVENT_PROCESS, 'Process Published Fixed-Income Debt Event', { makerChecker: true, requestedScope: { portfolioId: [{ source: 'body', field: 'portfolioAccountId' }] } }),
    [REPORTING_FIXED_INCOME_LIFECYCLE_APPROVE]: portfolioOperation(REPORTING_FIXED_INCOME_LIFECYCLE_APPROVE, 'Approve Fixed-Income Lifecycle Event', { makerChecker: true, requestedScope: branchMutationScope() }),
    [REPORTING_FIXED_INCOME_LIFECYCLE_POST]: portfolioOperation(REPORTING_FIXED_INCOME_LIFECYCLE_POST, 'Post Fixed-Income Lifecycle Event', { makerChecker: true, requestedScope: branchMutationScope() }),
    [REPORTING_FIXED_INCOME_LIFECYCLE_SCHEDULE]: portfolioOperation(REPORTING_FIXED_INCOME_LIFECYCLE_SCHEDULE, 'Schedule Fixed-Income Lifecycle Event', { makerChecker: true, requestedScope: { portfolioId: [{ source: 'body', field: 'portfolioAccountId' }] } }),
    [REPORTING_FIXED_INCOME_VALUATION_CALCULATE]: portfolioOperation(REPORTING_FIXED_INCOME_VALUATION_CALCULATE, 'Calculate Fixed-Income Valuation', { requestedScope: { portfolioId: [{ source: 'body', field: 'portfolioAccountId' }] } }),
    [REPORTING_PERFORMANCE_RETURN_CORRECTION_REQUEST]: portfolioOperation(REPORTING_PERFORMANCE_RETURN_CORRECTION_REQUEST, 'Request Performance Return Correction', { makerChecker: true, requestedScope: branchMutationScope() }),
    [REPORTING_PERFORMANCE_RETURN_CORRECTION_APPROVE]: portfolioOperation(REPORTING_PERFORMANCE_RETURN_CORRECTION_APPROVE, 'Approve Performance Return Correction', { makerChecker: true, requestedScope: branchMutationScope() }),
    [REPORTING_PERFORMANCE_RETURN_CORRECTION_FINALIZE]: portfolioOperation(REPORTING_PERFORMANCE_RETURN_CORRECTION_FINALIZE, 'Finalize Performance Return Correction', { makerChecker: true, requestedScope: branchMutationScope() }),
    [REPORTING_PERFORMANCE_RETURN_CALCULATE]: portfolioOperation(REPORTING_PERFORMANCE_RETURN_CALCULATE, 'Calculate Performance Return', { requestedScope: { portfolioId: [{ source: 'body', field: 'portfolioAccountId' }] } }),
    [REPORTING_PERFORMANCE_RETURN_LIST]: portfolioOperation(REPORTING_PERFORMANCE_RETURN_LIST, 'List Performance Returns', { readOnly: true, aal: AuthenticationAssuranceLevel.AAL2, requestedScope: { portfolioId: [{ source: 'query', field: 'portfolioAccountId' }] } }),
    [PORTFOLIO_PERFORMANCE_LIST]: portfolioOperation(PORTFOLIO_PERFORMANCE_LIST, 'List Portfolio Performance', {
        module: 'INVESTMENT',
        readOnly: true,
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        actorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        supportDelegationEligible: true,
        delegationPolicy: 'EXPLICITLY_GRANTED',
        requestedScope: {
            clientAccountId: [{ source: 'query', field: 'filters.clientAccountId' }],
            portfolioId: [{ source: 'query', field: 'filters.portfolioAccountId' }],
        },
    }),
    [PORTFOLIO_PERFORMANCE_EXPORT]: portfolioOperation(PORTFOLIO_PERFORMANCE_EXPORT, 'Export Portfolio Performance', {
        module: 'INVESTMENT',
        permission: 'organization:reports:export',
        permissionId: 'organization:reports:export',
        exportOnly: true,
        requestedScope: {
            clientAccountId: [{ source: 'query', field: 'clientAccountId' }],
            portfolioId: [{ source: 'query', field: 'portfolioAccountId' }],
        },
    }),
    [CLIENT_PORTFOLIO_PERFORMANCE_READ_OWN]: {
        id: CLIENT_PORTFOLIO_PERFORMANCE_READ_OWN,
        name: 'Read Own Portfolio Performance',
        labelKey: 'operations.portfolioPerformance.clientReadOwn.label',
        descriptionKey: 'operations.portfolioPerformance.clientReadOwn.description',
        permission: 'client:reports:read-own',
        permissionId: 'client:reports:read-own',
        module: 'INVESTMENT',
        requiredModules: ['INVESTMENT', 'REPORTING'],
        requiresEntitledModule: true,
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.CLIENT, ScopeType.PORTFOLIO],
        allowedScopeKinds: ['CLIENT', 'PORTFOLIO'],
        allowedPersonas: ['ORG_CLIENT'],
        ownershipPolicy: 'OWN_CLIENT',
        clientForcing: 'REQUIRED',
        requestedScope: { portfolioId: [{ source: 'query', field: 'filters.portfolioAccountId' }] },
        forcesClientScope: true,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'portfolio.performance.client.read-own',
        allowedActorTypes: [ActorType.CLIENT],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: false,
        delegationPolicy: 'NOT_ALLOWED',
        accessType: 'READ',
    },
};
